import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import notificationService from '../../../../services/notificationService';
import {
  reportsApi, type ReportRowDto, type ReportResponseDto,
  clientApi, type ClientDto,
  userApi, type UserDto,
} from 'api/services/timeTrackerApi';
import { formatHM } from '../tasks/duration';

const fmtMoney = (n: number | null | undefined) =>
  n == null || isNaN(Number(n)) ? '—' : Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const firstOfMonthIso = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const todayIso = () => new Date().toISOString().slice(0, 10);

const ReportsScreen: React.FC = () => {
  const [dateFrom, setDateFrom] = useState<string>(firstOfMonthIso());
  const [dateTo, setDateTo] = useState<string>(todayIso());
  const [userId, setUserId] = useState<string>('');
  const [clientId, setClientId] = useState<string>('');

  const [report, setReport] = useState<ReportResponseDto | null>(null);
  const [loading, setLoading] = useState(false);

  const [clients, setClients] = useState<ClientDto[]>([]);
  const [users, setUsers] = useState<UserDto[]>([]);

  // Lookup dropdowns
  useEffect(() => {
    (async () => {
      try {
        const c = await clientApi.list({ pageSize: 200 });
        setClients(c.items);
      } catch { /* non-fatal */ }
      try {
        const u = await userApi.list({ pageSize: 200 });
        setUsers(u.items);
      } catch {
        // Non-admin users get 403 here — filter just stays empty.
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await reportsApi.time({
        dateFrom: dateFrom || undefined,
        dateTo:   dateTo   || undefined,
        userId:   userId   || undefined,
        clientId: clientId || undefined,
      });
      setReport(data);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err.message || 'Failed to load report.');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, userId, clientId]);

  useEffect(() => {
    const t = setTimeout(() => { load(); }, 250);
    return () => clearTimeout(t);
  }, [load]);

  const actions: ActionButton[] = [
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: load, tooltip: 'Refresh', variant: 'icon' },
  ];

  // SyutGrid needs an `id` per row — synthesise from (userId + taskId).
  type GridRow = ReportRowDto & { id: string };
  const rows: GridRow[] = useMemo(
    () => (report?.rows ?? []).map(r => ({ ...r, id: `${r.userId}-${r.taskId}` })),
    [report]
  );

  const columns: SyutGridColumn<GridRow>[] = useMemo(() => [
    { id: 'userName',   header: 'User',     accessorKey: 'userName',   size: 180,
      cell: ({ getValue }) => (
        <span style={{ fontWeight: 600 }}>{(getValue() as string) || '—'}</span>
      ),
    },
    { id: 'clientName', header: 'Client',   accessorKey: 'clientName', size: 220 },
    { id: 'taskTitle',  header: 'Task',     accessorKey: 'taskTitle',  size: 280 },
    { id: 'totalHours', header: 'Duration', accessorKey: 'totalHours', size: 140,
      cell: ({ getValue }) => (
        <strong style={{ color: 'var(--primary)' }}>{formatHM(Number(getValue() ?? 0))}</strong>
      ),
    },
    { id: 'entryCount', header: 'Entries', accessorKey: 'entryCount', size: 100,
      cell: ({ getValue }) => Number(getValue() ?? 0).toLocaleString(),
    },
    { id: 'totalAmount', header: 'Amount', accessorKey: 'totalAmount', size: 130,
      cell: ({ getValue }) => {
        const v = Number(getValue() ?? 0);
        return v === 0
          ? <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }} title="No rate configured for the user on these entry dates.">0.00</span>
          : <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(v)}</strong>;
      },
    },
  ], []);

  // ── Summary cards ───────────────────────────────────────────────────
  const summary = report?.summary;
  const summaryCards = useMemo(() => ([
    { label: 'Total time',   value: summary ? formatHM(summary.totalHours)  : '—', icon: <IconSystem.clock      size={20} /> },
    { label: 'Total amount', value: summary ? fmtMoney(summary.totalAmount) : '—', icon: <IconSystem.dollarSign size={20} /> },
    { label: 'Users',        value: summary?.userCount?.toLocaleString()   ?? '—', icon: <IconSystem.users      size={20} /> },
    { label: 'Tasks',        value: summary?.taskCount?.toLocaleString()   ?? '—', icon: <IconSystem.list       size={20} /> },
    { label: 'Time entries', value: summary?.entryCount?.toLocaleString()  ?? '—', icon: <IconSystem.calendar   size={20} /> },
  ]), [summary]);

  // ── Range presets ───────────────────────────────────────────────────
  const setThisMonth = () => { setDateFrom(firstOfMonthIso()); setDateTo(todayIso()); };
  const setLast30    = () => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    setDateFrom(d.toISOString().slice(0, 10));
    setDateTo(todayIso());
  };
  const setThisYear  = () => {
    const d = new Date();
    setDateFrom(new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10));
    setDateTo(todayIso());
  };
  const setAllTime   = () => { setDateFrom(''); setDateTo(''); };

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Time Report"
        description="Time logged per user × client × task, summed over the date range."
        icon={<IconSystem.chart size={18} />}
        actions={actions}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', padding: '0.75rem', gap: '0.75rem' }}>

          {/* Filter bar */}
          <div style={{
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: '0.5rem',
            padding: '0.75rem 1rem',
          }}>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ width: 150 }}>
                <label className="form-label">From</label>
                <input
                  type="date"
                  className="form-input"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>

              <div style={{ width: 150 }}>
                <label className="form-label">To</label>
                <input
                  type="date"
                  className="form-input"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                <button type="button" className="btn btn-secondary" style={{ padding: '0.375rem 0.625rem', fontSize: '0.75rem' }} onClick={setThisMonth}>This month</button>
                <button type="button" className="btn btn-secondary" style={{ padding: '0.375rem 0.625rem', fontSize: '0.75rem' }} onClick={setLast30}>Last 30 d</button>
                <button type="button" className="btn btn-secondary" style={{ padding: '0.375rem 0.625rem', fontSize: '0.75rem' }} onClick={setThisYear}>This year</button>
                <button type="button" className="btn btn-secondary" style={{ padding: '0.375rem 0.625rem', fontSize: '0.75rem' }} onClick={setAllTime}>All time</button>
              </div>

              <div style={{ width: 200 }}>
                <label className="form-label">User</label>
                <SyutSelect
                  options={[{ value: '', label: 'All users' }, ...users.map(u => ({ value: u.id, label: u.username }))]}
                  value={userId}
                  onChange={(v) => setUserId((v as string) || '')}
                  placeholder="All users"
                  isClearable
                  size="sm"
                />
              </div>

              <div style={{ width: 200 }}>
                <label className="form-label">Client</label>
                <SyutSelect
                  options={[{ value: '', label: 'All clients' }, ...clients.map(c => ({ value: c.id, label: c.name }))]}
                  value={clientId}
                  onChange={(v) => setClientId((v as string) || '')}
                  placeholder="All clients"
                  isClearable
                  size="sm"
                />
              </div>
            </div>
          </div>

          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.625rem' }}>
            {summaryCards.map(c => (
              <div key={c.label} style={{
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                padding: '0.875rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: 'rgba(37,99,235,0.08)',
                  color: 'var(--primary)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {c.icon}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                    {c.label}
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                    {c.value}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Result table */}
          <div style={{ flex: 1, minHeight: 280 }}>
            <SyutGrid
              data={rows}
              columns={columns}
              enablePagination={false}
              emptyMessage={loading ? 'Loading…' : 'No data for the chosen filters.'}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsScreen;
